import { z } from 'zod';
import { SandboxOptionsSchema } from '@runloop/reflex-contract';
import { AgentDaemonSchema } from './agent.types.js';

/**
 * Host-owned sandbox-control tools, callable from inside a devbox.
 *
 * The on-box `reflex-service sandbox <verb>` CLI POSTs `{ tool, params }` to
 * the flex-bridge's localhost IPC `/tool` endpoint, which relays it to Reflex
 * as a `tool.call` frame over the per-agent control socket. The host resolves
 * the calling agent from that socket (never from the params), so every tool
 * here acts on the caller's own sandbox only. Results come back on the
 * correlated `tool.result` frame and the CLI prints them as JSON.
 *
 * The prefix is the segment before the first `_`; the host dispatches on it
 * (`SandboxBridgeRelayContract.registerToolCallHandler`), the same seam the
 * `workstation_*` tools use.
 */
export const SANDBOX_CONTROL_TOOL_PREFIX = 'sandbox';

export const SANDBOX_CONTROL_TOOLS = {
  /** Suspend the caller's devbox (disk kept, processes killed). */
  suspend: 'sandbox_suspend',
  /** The devbox's public tunnel hostname (and URL for a given port). */
  hostname: 'sandbox_hostname',
  /** The egress network policy the devbox was launched with. */
  network: 'sandbox_network',
  /** Everything: agent launch config, live devbox info, hostname, network. */
  config: 'sandbox_config',
} as const;

export type SandboxControlTool = (typeof SANDBOX_CONTROL_TOOLS)[keyof typeof SANDBOX_CONTROL_TOOLS];

/**
 * Every tool name the host answers under the {@link SANDBOX_CONTROL_TOOL_PREFIX}
 * prefix. The handler rejects anything else with a `tool.result` error, so a
 * typo'd verb fails loudly on the box instead of hanging.
 */
export const SandboxControlToolSchema = z.enum([
  SANDBOX_CONTROL_TOOLS.suspend,
  SANDBOX_CONTROL_TOOLS.hostname,
  SANDBOX_CONTROL_TOOLS.network,
  SANDBOX_CONTROL_TOOLS.config,
]);

// --- params ---

/** `sandbox_suspend` takes no params; an empty object (or nothing) is accepted. */
export const SandboxSuspendParamsSchema = z.object({}).passthrough().optional();

/**
 * `sandbox_hostname` optionally takes the port whose public URL the caller
 * wants. Without it the response still carries the tunnel key/domain and the
 * hostname template, so the caller can build a URL for any port itself.
 */
export const SandboxHostnameParamsSchema = z
  .object({
    port: z.number().int().min(1).max(65535).optional(),
  })
  .optional();

export const SandboxNetworkParamsSchema = z.object({}).passthrough().optional();
export const SandboxConfigParamsSchema = z.object({}).passthrough().optional();

// --- results ---

export const SandboxSuspendResultSchema = z.object({
  devboxId: z.string(),
  /**
   * `suspending_at_turn_end`: a turn is in flight, so the host stamped the
   * intent on the agent and suspends once the turn-ended boundary has been
   * delivered (the agent can keep working until its final message).
   * `suspending`: no turn in flight; the suspend is issued after `graceMs`.
   */
  status: z.enum(['suspending_at_turn_end', 'suspending']),
  /** Milliseconds before an immediate suspend is issued (0 when deferred to turn end). */
  graceMs: z.number().int().nonnegative(),
  /** Human-readable reminder of what a suspend means for the agent. */
  note: z.string(),
});
export type SandboxSuspendResult = z.infer<typeof SandboxSuspendResultSchema>;

export const SandboxHostnameResultSchema = z.object({
  /** The per-devbox tunnel key: the `{key}` in `{port}-{key}.{domain}`. */
  tunnelKey: z.string(),
  /** The tunnel domain the devbox's ports are published under. */
  tunnelDomain: z.string(),
  /** `{port}-{tunnelKey}.{tunnelDomain}` with `{port}` left for the caller to fill. */
  hostnameTemplate: z.string(),
  /** Present when the caller passed a port. */
  port: z.number().int().optional(),
  hostname: z.string().optional(),
  url: z.string().optional(),
});
export type SandboxHostnameResult = z.infer<typeof SandboxHostnameResultSchema>;

/** Egress rules, normalised from the provider's policy view. */
export const SandboxNetworkPolicySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  allowAll: z.boolean(),
  allowAgentGateway: z.boolean(),
  allowMcpGateway: z.boolean(),
  allowRunloopMirrors: z.boolean(),
  allowDevboxToDevbox: z.boolean(),
  allowedHostnames: z.array(z.string()),
  allowedCidrs: z.array(
    z.object({
      cidr: z.string(),
      ports: z.array(
        z.object({
          port: z.number().int(),
          endPort: z.number().int().nullable(),
          protocol: z.enum(['TCP', 'UDP']).nullable(),
        }),
      ),
    }),
  ),
});
export type SandboxNetworkPolicy = z.infer<typeof SandboxNetworkPolicySchema>;

export const SandboxNetworkResultSchema = z.object({
  devboxId: z.string(),
  /** The policy id the devbox was launched with, or null when no policy applies (all egress allowed). */
  networkPolicyId: z.string().nullable(),
  /**
   * Resolved policy. Null when no policy applies, or when `networkPolicyId` is
   * set but the provider could not read it; `summary` tells the two apart.
   */
  policy: SandboxNetworkPolicySchema.nullable(),
  /** One-line summary of the effective egress rules. */
  summary: z.string(),
});
export type SandboxNetworkResult = z.infer<typeof SandboxNetworkResultSchema>;

/**
 * Live devbox facts read from the provider, normalised so the on-box caller
 * never sees a raw provider view.
 */
export const SandboxDevboxInfoSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  blueprintId: z.string().nullable(),
  snapshotId: z.string().nullable(),
  capabilities: z.array(z.string()),
  architecture: z.string().nullable(),
  resourceSize: z.string().nullable(),
  customCpuCores: z.number().nullable(),
  customGbMemory: z.number().nullable(),
  customDiskSize: z.number().nullable(),
  /** Seconds of inactivity before the provider auto-suspends the box, when configured. */
  idleSuspendAfterSeconds: z.number().nullable(),
  keepAliveTimeSeconds: z.number().nullable(),
  availablePorts: z.array(z.number()),
  networkPolicyId: z.string().nullable(),
  tunnelAuthMode: z.string().nullable(),
  createdAt: z.number().nullable(),
});
export type SandboxDevboxInfo = z.infer<typeof SandboxDevboxInfoSchema>;

export const SandboxConfigResultSchema = z.object({
  agent: z.object({
    id: z.string(),
    name: z.string(),
    agentType: z.string(),
    status: z.string(),
    model: z.string().nullable(),
    /** The effective sandbox options the agent was launched with (blueprint, size, dockerd, ...). */
    sandboxOptions: SandboxOptionsSchema.nullable(),
    /** Services registered with `reflex-service register`, as Reflex sees them. */
    daemons: z.array(AgentDaemonSchema),
  }),
  /** Null when the devbox could not be described (no devbox yet, or the lookup failed). */
  devbox: SandboxDevboxInfoSchema.nullable(),
  /** Null when the agent has no tunnel key yet. */
  hostname: SandboxHostnameResultSchema.nullable(),
  network: SandboxNetworkResultSchema.nullable(),
  bridge: z.object({
    controlPort: z.number().int(),
    ipcPort: z.number().int(),
    /** Root of the on-box `reflex-service` state (services, logs, bin). */
    reflexRoot: z.string(),
  }),
});
export type SandboxConfigResult = z.infer<typeof SandboxConfigResultSchema>;
